import React, { useState, useEffect } from "react";

import {
  CheckCircle2,
  AlertCircle,
  ExternalLink,
  Download,
} from "lucide-react";
import Dialog from "@mui/material/Dialog";
import DialogTitle from "@mui/material/DialogTitle";
import DialogContent from "@mui/material/DialogContent";
import DialogActions from "@mui/material/DialogActions";
import Button from "@mui/material/Button";
import IconButton from "@mui/material/IconButton";
import Box from "@mui/material/Box";
import Typography from "@mui/material/Typography";
import Table from "@mui/material/Table";
import TableBody from "@mui/material/TableBody";
import TableCell from "@mui/material/TableCell";
import TableContainer from "@mui/material/TableContainer";
import TableHead from "@mui/material/TableHead";
import TableRow from "@mui/material/TableRow";
import CloseIcon from "@mui/icons-material/Close";

import { DataGridPro , useGridApiRef} from '@mui/x-data-grid-pro';

const BatchSendResultDialog = ({ open, handleClose, failedInvoices, successfulInvoices }) => {
  const columns = [
    { flex: 1, field: "invoiceID", headerName: "Invoice ID" },
    { flex: 1.5, field: "failureReason", headerName: "Failure Reason" },
  ];

  const allSuccess = successfulInvoices.length > 0 && failedInvoices <= 0;

  const handleDownloadFailedInvoices = () => {
    if (failedInvoices.length === 0) return;

    const headers = ["Invoice ID", "Customer Name", "Status", "Failure Reason"];
    const csvContent = [
      headers.join(","),
      ...failedInvoices.map((invoice) =>
        [
          `"${invoice.invoiceID}"`,
          `"${
            invoice.failureReason || "Unable to send invoice. Please try again."
          }"`,
        ].join(",")
      ),
    ].join("\n");

    const blob = new Blob([csvContent], { type: "text/csv;charset=utf-8;" });
    const url = URL.createObjectURL(blob);
    const link = document.createElement("a");
    link.href = url;
    link.download = `failed-invoices-${
      new Date().toISOString().split("T")[0]
    }.csv`;
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    URL.revokeObjectURL(url);
  };

  return (
    <Dialog
      open={open}
      onClose={handleClose}
      maxWidth="sm"
      fullWidth
      PaperProps={{
        sx: {
          maxHeight: "90vh",
          display: "flex",
          flexDirection: "column",
        },
      }}
    >
      <DialogTitle
        // sx={{
        //   display: "flex",
        //   alignItems: "center",
        //   justifyContent: "space-between",
        //   borderBottom: "1px solid #e9ecef",
        //   px: 3,
        //   py: 2,
        // }}
        sx={{
            display: "flex",
            alignItems: "center",
            justifyContent: "space-between",
            borderBottom: "1px solid #D9D9D9",
            paddingBottom: "20px !important",
          }}
      >
        <Box sx={{ display: "flex", alignItems: "center", gap: 1.5 }}>
          <Box
            sx={{
              width: 40,
              height: 40,
              borderRadius: "50%",
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              backgroundColor: allSuccess ? "#e6f7f1" : "#fff3cd",
            }}
          >
            {allSuccess ? (
              <CheckCircle2 size={20} color="#1BA97D" />
            ) : (
              <AlertCircle size={20} color="#856404" />
            )}
          </Box>
          <Typography
            variant='h4'
            fontWeight="bold"
            // sx={{
            //   fontSize: "1.25rem",
            //   fontWeight: 600,
            //   color: "#212529",
            // }}
          >
            Batch Send Completed
          </Typography>
        </Box>
        <IconButton onClick={handleClose} size="small" sx={{ color: "#6c757d" }}>
          <CloseIcon fontSize="small" />
        </IconButton>
      </DialogTitle>
      <DialogContent sx={{ px: 3, py: 2.5, flex: 1, overflow: "auto" }}>
        <Box
          sx={{
            display: "grid",
            gridTemplateColumns: "1fr 1fr",
            gap: 2,
            mb: 2.5,
          }}
        >
          <Box
            sx={{
              backgroundColor: "#e6f7f1",
              borderRadius: "12px",
              p: 2,
              border: "1px solid rgba(27, 169, 125, 0.2)",
            }}
          >
            <Box
              sx={{ display: "flex", alignItems: "center", gap: 1, mb: 0.5 }}
            >
              <CheckCircle2 size={16} color="#1BA97D" />
              <Typography
                sx={{ fontSize: "14px", fontWeight: 500, color: "#1BA97D" }}
              >
                Successfully Sent
              </Typography>
            </Box>
            <Typography
              sx={{ fontSize: "1.875rem", fontWeight: 700, color: "#1BA97D" }}
            >
              {successfulInvoices.length}
            </Typography>
          </Box>
          <Box
            sx={{
              backgroundColor: failedInvoices.length > 0 ? "#fdeaec" : "#f8f9fa",
              borderRadius: "12px",
              p: 2,
              border: `1px solid ${
                failedInvoices.length > 0 ? "rgba(220, 53, 69, 0.2)" : "#dee2e6"
              }`,
            }}
          >
            <Box
              sx={{ display: "flex", alignItems: "center", gap: 1, mb: 0.5 }}
            >
              <AlertCircle
                size={16}
                color={failedInvoices.length > 0 ? "#dc3545" : "#6c757d"}
              />
              <Typography
                sx={{
                  fontSize: "14px",
                  fontWeight: 500,
                  color: failedInvoices.length > 0 ? "#dc3545" : "#6c757d",
                }}
              >
                Failed to Send
              </Typography>
            </Box>
            <Typography
              sx={{
                fontSize: "1.875rem",
                fontWeight: 700,
                color: failedInvoices.length > 0 ? "#dc3545" : "#6c757d",
              }}
            >
              {failedInvoices.length}
            </Typography>
          </Box>
        </Box>
        {allSuccess ? (
          <Box
            sx={{
              backgroundColor: "#e6f7f1",
              borderRadius: "12px",
              p: 2,
              textAlign: "center",
              border: "1px solid rgba(27, 169, 125, 0.2)",
            }}
          >
            <CheckCircle2
              size={32}
              color="#1BA97D"
              style={{ marginBottom: 8 }}
            />
            <Typography sx={{ color: "#1BA97D", fontWeight: 500 }}>
              All invoices were sent successfully.
            </Typography>
          </Box>
        ) : (
          <>
            <Typography sx={{ fontSize: "14px", color: "#6c757d", mb: 2 }}>
              Failed invoices were not updated and remain unchanged.
            </Typography>
            <Box
              sx={{
                border: "1px solid #dee2e6",
                borderRadius: "12px",
                overflow: "hidden",
              }}
            >
              <Box
                sx={{
                  backgroundColor: "#f8f9fa",
                  px: 2,
                  py: 4,
                  borderBottom: "1px solid #dee2e6",
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "space-between",
                }}
              >
                <Typography
                  sx={{ fontWeight: 600, color: "#212529", fontSize: "14px" }}
                >
                  Failed Invoices
                </Typography>
                <Button
                  variant="outlined"
                  size="small"
                  onClick={handleDownloadFailedInvoices}
                  startIcon={<Download size={14} />}
                  sx={{
                    borderColor: "#dee2e6",
                    color: "#495057",
                    fontSize: "12px",
                    py: 0.5,
                    "&:hover": {
                      backgroundColor: "#e9ecef",
                      borderColor: "#dee2e6",
                    },
                  }}
                >
                  Download CSV
                </Button>
              </Box>
              <DataGridPro
                rows={failedInvoices}
                columns={columns}
                rowHeight={62}
                disableRowSelectionOnClick
                sx={{
                    "& .MuiDataGrid-columnHeaderTitle": {
                        textTransform: "none",
                    },
                }}
              />
            </Box>
          </>
        )}
      </DialogContent>
      <DialogActions
        // sx={{
        //   px: 3,
        //   py: 2,
        //   borderTop: "1px solid #e9ecef",
        //   backgroundColor: "#f8f9fa",
        // }}
        sx={{
            display: "flex",
            flexDirection: { xs: "column", sm: "row" },
            padding: "20px 24px !important",
            boxShadow: `0px -4px 8px rgb(0, 0, 0, 0.1)`,
            zIndex: 1,
            gap: 2,
          }}
      >
        <Button
          variant="contained"
          color="info"
          onClick={handleClose}
        >
          Close
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default BatchSendResultDialog;